import { useState, useEffect, useRef } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function Support() {
  const { user } = useApp();
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('chat');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadMessages, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  async function loadData() {
    await Promise.all([loadMessages(), loadBroadcasts()]);
    setLoading(false);
  }

  async function loadMessages() {
    try {
      const { data } = await api.get('/support/my-chat');
      setMessages(data || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadBroadcasts() {
    try {
      const { data } = await api.get('/support/broadcasts');
      setBroadcasts(data || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function sendMessage() {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await api.post('/support/send', { message: newMessage.trim() });
      setNewMessage('');
      await loadMessages();
    } catch (e) {
      alert(e.userMessage || t('common.error'));
    } finally {
      setSending(false);
    }
  }

  function scrollToBottom() {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  function formatTime(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100%', paddingBottom: 0 }}>
      <BackButton />
      <h1>📞 {t('support.title')}</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className={`btn ${tab === 'chat' ? 'btn-primary' : ''}`}
          style={{ flex: 1, background: tab === 'chat' ? undefined : 'rgba(255,255,255,0.1)' }}
          onClick={() => setTab('chat')}
        >
          💬 {t('support.tab_chat')}
        </button>
        <button
          className={`btn ${tab === 'news' ? 'btn-primary' : ''}`}
          style={{ flex: 1, background: tab === 'news' ? undefined : 'rgba(255,255,255,0.1)' }}
          onClick={() => setTab('news')}
        >
          📢 {t('support.tab_news')} ({broadcasts.length})
        </button>
      </div>

      {tab === 'chat' ? (
        <>
          <div style={{
            flex: 1,
            overflowY: 'auto',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 12,
            padding: 12,
            marginBottom: 12,
            minHeight: 200,
          }}>
            {messages.length === 0 ? (
              <div style={{ textAlign: 'center', opacity: 0.5, padding: 32 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
                <p>{t('support.empty_chat')}</p>
              </div>
            ) : (
              messages.map(m => (
                <div
                  key={m.id}
                  style={{
                    display: 'flex',
                    justifyContent: m.sender_type === 'user' ? 'flex-end' : 'flex-start',
                    marginBottom: 8,
                  }}
                >
                  <div style={{
                    maxWidth: '80%',
                    padding: '10px 14px',
                    borderRadius: 12,
                    background: m.sender_type === 'user'
                      ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                      : 'rgba(255,255,255,0.1)',
                  }}>
                    <div style={{ fontSize: 14 }}>{m.message}</div>
                    <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, textAlign: 'right' }}>
                      {formatTime(m.created_at)}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <div style={{ display: 'flex', gap: 8, paddingBottom: 16 }}>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder={t('support.placeholder')}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
              }}
            />
            <button
              className="btn btn-primary"
              onClick={sendMessage}
              disabled={sending || !newMessage.trim()}
            >
              {sending ? '⏳' : '📤'}
            </button>
          </div>
        </>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {broadcasts.length === 0 ? (
            <div style={{ textAlign: 'center', opacity: 0.5, padding: 32 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📢</div>
              <p>{t('support.no_news')}</p>
            </div>
          ) : (
            broadcasts.map(b => (
              <div key={b.id} style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 12,
                padding: 16,
                marginBottom: 12,
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{b.title}</div>
                <div style={{ fontSize: 14, opacity: 0.9, whiteSpace: 'pre-wrap' }}>{b.message}</div>
                <div style={{ fontSize: 11, opacity: 0.5, marginTop: 8 }}>{formatTime(b.created_at)}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
